/**
 * Execution Agent
 * 
 * LLM-powered agent that builds slides area-by-area.
 * Called by MultiSlideBuilderAgent for each section.
 * 
 * ## Architecture
 * 
 * The Execution Agent:
 * 1. Inserts slides by copying from source and clearing content (in beforeExecute)
 * 2. Receives pre-specified areas to build for each slide
 * 3. Uses LLM to build each area using buildArea tool
 * 4. Evaluates screenshots to verify builds
 * 5. Calls submitResults when all slides are complete
 * 
 * ## Key Features
 * 
 * - **Pre-specified areas**: Caller determines regions, agent builds them
 * - **Two-level context**: Global context + per-slide context
 * - **Multi-slide support**: Builds multiple slides in one agent run
 * - **Auto-checkpointing**: Every buildArea creates a 5-digit checkpoint
 * - **Screenshot evaluation**: LLM can see and verify results
 * - **1M token context**: Extended context for large sections
 * - **No streaming**: Static LLM calls for reliability
 * - **submitResults pattern**: Structured completion with results
 */

import { z } from "zod";
import { AgentBaseClass } from "../base/agentBaseClass";
import { ToolDescription } from "../base/toolDescription";
import type { SubmitResultsParams } from "../base/submitResults.schema";
import { ChatMessage } from "../../globals/llm/baseAdapter";
import { getMessageBuilder } from "../../globals/llm/messageBuilder";
import { getMessagePreparer, REMINDER_IMAGE_MARKER } from "../../globals/llm/reminder";
import {
    AgentResult,
    ResultItem,
    ResolvedContextItem,
    ThreadId,
    CancellationToken,
    AgentCallbacks,
} from "../../core/types";

import {
    ExecutionAgentParamsSchema,
    type ExecutionAgentParams,
    type ExecutionAgentResponse,
    type AreaBuildResult,
    type SlideBuildResult,
    type SlideToBuildWithIndex,
} from "./executionAgent.schema";

import { BackgroundBuildAreaTool } from "./tools/backgroundBuildArea";
import { BuilderReadSlideTool } from "./tools/builderReadSlide";
import { RollbackTool } from "./tools/rollback";
import type { BuildAreaResponse } from "./tools/backgroundBuildArea/backgroundBuildArea.schema";

// Shared prompt content
import {
    DENSITY_SECTION_FULL,
    CHART_SOPHISTICATION,
    TABLE_SOPHISTICATION,
    STYLE_CONSISTENCY,
    RULES_FOR_SLIDE_CONSTRUCTION,
    ARTIFACT_ID_USAGE,
    generateSlideStateFeedbackContent,
    generateAvailableSlidesSection,
    IMAGE_CONTEXT_PREFIXES,
    ensureDataUrl,
    extractBase64FromDataUrl,
} from "./prompts";
import { backendClient } from "../../../services/communication/backendClient";
import { getContextResolver } from "../../core/contextResolver";
import type { ContextItem as CoreContextItem, ResolvedContent } from "../../core/types";
import type { ContextItem } from "./executionAgent.schema";

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT RESOLUTION TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface ResolvedContextData {
    /** ID -> resolved content (text or metadata) */
    contentById: Map<string, ResolvedContent | null>;
    /** IDs that have images (logo_, wikimedia_, artifact_, asset_) */
    imageIds: string[];
    /** Formatted text summary for prompt */
    textSummary: string;
}

/** Content part for multimodal messages */
interface TextContentPart {
    type: 'text';
    text: string;
}

interface ImageContentPart {
    type: 'image_url';
    image_url: {
        url: string;
        detail?: 'auto' | 'low' | 'high';
    };
}

// ═══════════════════════════════════════════════════════════════════════════
// EXECUTION AGENT
// ═══════════════════════════════════════════════════════════════════════════

export class ExecutionAgent extends AgentBaseClass<ExecutionAgentParams, AgentResult<ExecutionAgentResponse>> {
    
    // ═══════════════════════════════════════════════════════════════════════
    // REQUIRED IMPLEMENTATIONS
    // ═══════════════════════════════════════════════════════════════════════
    
    readonly agentType = 'executionAgent';
    readonly name = 'executionAgent';
    readonly isAgent = true;
    
    // Schema for validation
    public paramsSchema = ExecutionAgentParamsSchema as unknown as z.ZodType<ExecutionAgentParams>;
    
    // ═══════════════════════════════════════════════════════════════════════
    // INFRASTRUCTURE FLAGS
    // ═══════════════════════════════════════════════════════════════════════
    
    protected useThreadManagement = false;  // Standalone agent (no registry needed)
    protected useTaskTracking = false;      // No registry dependency
    protected useContextResolution = false; // Context passed directly
    protected useSubmitResultsPattern = true;  // Use submitResults for structured completion
    protected stream = false;               // No streaming - static calls
    // maxResponseTokens: inherits 0 from base → adapter uses model maximum
    protected maxToolLoopIterations = 200;  // Many iterations for area-by-area
    
    // Enable 1M token context
    protected extendedContext = true;
    
    // ═══════════════════════════════════════════════════════════════════════
    // SESSION STATE
    // ═══════════════════════════════════════════════════════════════════════
    
    private planId: string;
    private params: ExecutionAgentParams | null = null;
    
    /** Slides with actual indices after insertion */
    private slidesToBuild: SlideToBuildWithIndex[] = [];
    
    /** Per-slide build results */
    private slideResults: Map<number, SlideBuildResult> = new Map();
    
    /** Area-by-area results (detailed tracking) */
    private areaResults: AreaBuildResult[] = [];
    
    /** Image IDs resolved from context (to be included in initial user message) */
    private resolvedImageIds: string[] = [];
    
    /** Reference slide data (from hidden style guide slides) */
    private referenceSlideData: Map<number, { slideData: unknown; imageBase64: string }> = new Map();
    
    // ═══════════════════════════════════════════════════════════════════════
    // CONSTRUCTOR
    // ═══════════════════════════════════════════════════════════════════════
    
    constructor(planId: string) {
        super();
        this.planId = planId;
        
        // Initialize tools with plan ID
        this.toolCalls = [
            new BackgroundBuildAreaTool(planId),
            new BuilderReadSlideTool(planId, this.params?.styleGuideSlideCount ?? 0),
            new RollbackTool(planId),
            // submitResults is auto-injected by useSubmitResultsPattern
        ];
        
        this.description = new ToolDescription(
            "executionAgent",
            `Build slides area-by-area with pre-specified areas and context.
            
Receives:
- Slides to build with pre-specified areas (regions in 0-1 normalized coordinates)
- Global context (applies to all slides)
- Per-slide context (specific data/references)
- Reference slide indices for styling

Process:
1. Slides are inserted at instantiation (copied from source, content cleared)
2. For each slide, build each area using buildArea tool
3. Evaluate screenshots to verify quality
4. Call submitResults when complete

Context IDs use standard prefixes:
- as_xxx: AlphaSense results
- slide_N: Reference slides
- excel_xxx: Excel data created
- excelread_xxx: Excel data read
- logo_xxx, wikimedia_xxx: Images`,
            ExecutionAgentParamsSchema
        );
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // LIFECYCLE HOOKS
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Called before execute - insert slides and store params
     * 
     * Slides are inserted by copying source slides and clearing content.
     * This happens BEFORE the LLM loop starts, so the LLM operates on actual indices.
     */
    protected async beforeExecute(context: {
        params: ExecutionAgentParams;
        threadId: ThreadId;
        taskId: string;
        cancellationToken: CancellationToken;
        resolvedContext: ResolvedContextItem[];
        conversationId: string;
        documentId: string;
        callbacks?: AgentCallbacks;
    }): Promise<void> {
        const params = context.params;
        this.params = params;
        this.slidesToBuild = [];
        this.slideResults = new Map();
        this.areaResults = [];
        
        console.log(`[ExecutionAgent] Starting: ${params.sectionTitle ?? 'Slide Build'}`);
        console.log(`[ExecutionAgent] Slides to build: ${params.slides.length}`);
        
        // Insert slides - prefer layout-based insertion, fall back to source-based
        for (const slideSpec of params.slides) {
            context.cancellationToken.throwIfCancelled();
            
            let actualIndex: number;
            
            // Prefer layout-based insertion (uses pre-processed layout-only PPTX)
            if (slideSpec.layoutId && slideSpec.documentId) {
                console.log(`[ExecutionAgent] Inserting slide from layout ${slideSpec.layoutId} (deck ${slideSpec.documentId}) at position ${slideSpec.position}`);
                
                const insertResult = await backendClient.insertSlideFromLayout(
                    params.planId,
                    {
                        documentId: slideSpec.documentId,
                        layoutId: slideSpec.layoutId,
                        targetPosition: slideSpec.position,
                    }
                );
                
                if (!insertResult.success) {
                    console.error(`[ExecutionAgent] Failed to insert slide from layout: ${insertResult.message}`);
                    throw new Error(`Failed to insert slide from layout: ${insertResult.message}`);
                }
                
                actualIndex = insertResult.actual_index;
                console.log(`[ExecutionAgent] Inserted layout-only slide at index ${actualIndex} from layout ${insertResult.layout_id}`);
                
            } else if (slideSpec.sourceSlideIndex !== undefined) {
                // Fall back to source-based insertion (DEPRECATED - tries to clear at runtime)
                console.warn(`[ExecutionAgent] Using DEPRECATED sourceSlideIndex. Consider using layoutId instead.`);
                console.log(`[ExecutionAgent] Inserting slide from source ${slideSpec.sourceSlideIndex} at position ${slideSpec.position}`);
                
                const insertResult = await backendClient.insertSlide(
                    params.planId,
                    {
                        sourceSlideIndex: slideSpec.sourceSlideIndex,
                        targetPosition: slideSpec.position,
                    }
                );
                
                if (!insertResult.success) {
                    console.error(`[ExecutionAgent] Failed to insert slide: ${insertResult.message}`);
                    throw new Error(`Failed to insert slide: ${insertResult.message}`);
                }
                
                actualIndex = insertResult.actual_index;
                console.log(`[ExecutionAgent] Inserted slide at index ${actualIndex} (cleared ${insertResult.shapes_cleared} shapes, ${insertResult.placeholders_cleared} placeholder texts)`);
                
            } else {
                throw new Error(`Slide spec must have either layoutId+documentId or sourceSlideIndex: ${JSON.stringify(slideSpec)}`);
            }
            
            // Track the slide with its actual index
            this.slidesToBuild.push({
                ...slideSpec,
                actualSlideIndex: actualIndex,
            });
            
            // Initialize slide result
            this.slideResults.set(actualIndex, {
                slideIndex: actualIndex,
                title: slideSpec.title,
                areasBuilt: 0,
                success: false, // Will be updated as areas are built
                errors: [],
            });
        }
        
        console.log(`[ExecutionAgent] All ${this.slidesToBuild.length} slides inserted, ready for building`);
        
        // Read reference slides (hidden style guide slides) if specified
        if (params.referenceSlideIndices && params.referenceSlideIndices.length > 0) {
            console.log(`[ExecutionAgent] Reading ${params.referenceSlideIndices.length} reference slides...`);
            await this.readReferenceSlides(params.referenceSlideIndices);
        }
    }
    
    /**
     * Read reference slides from the working PPTX (hidden style guide slides)
     * 
     * These are the style guide slides marked as hidden that serve as visual references.
     * We read them via the backend and store their images for inclusion in the context message.
     * 
     * NOTE: referenceSlideIndices are 1-based (user-facing), matching BuilderReadSlideTool.
     * We convert to 0-based PPTX indices for the backend call.
     */
    private async readReferenceSlides(referenceIndices: number[]): Promise<void> {
        const resolver = getContextResolver();
        
        for (const userIndex of referenceIndices) {
            // Convert 1-based user index to 0-based PPTX index
            const pptxIndex = userIndex - 1;
            
            try {
                const response = await backendClient.readWorkingSlide(this.planId, pptxIndex);
                
                if (response.success && response.screenshot_base64) {
                    // Store in our map using user index for consistency
                    this.referenceSlideData.set(userIndex, {
                        slideData: response.slide_data,
                        imageBase64: response.screenshot_base64,
                    });
                    
                    // Store image in contextResolver for easy access
                    const imageId = `ref_slide_${userIndex}`;
                    await resolver.storeImageAsync(
                        imageId,
                        extractBase64FromDataUrl(response.screenshot_base64),
                        'image/png',
                        {
                            type: 'reference_slide',
                            slideIndex: userIndex,
                            pptxIndex,
                            planId: this.planId,
                        }
                    );
                    
                    console.log(`[ExecutionAgent] Read reference slide ${userIndex} (PPTX ${pptxIndex}, ${response.slide_data?.shapes?.length || 0} shapes)`);
                } else {
                    console.warn(`[ExecutionAgent] Failed to read reference slide ${userIndex}: ${response.error || 'No screenshot'}`);
                }
            } catch (error) {
                console.error(`[ExecutionAgent] Error reading reference slide ${userIndex}:`, error);
            }
        }
        
        console.log(`[ExecutionAgent] Read ${this.referenceSlideData.size}/${referenceIndices.length} reference slides`);
    }
    
    /**
     * Convert normalized region (0-1) to points using actual slide dimensions
     */
    private regionToPoints(region: { x: number; y: number; width: number; height: number }): {
        left: number;
        top: number;
        width: number;
        height: number;
    } {
        const { width: slideWidth, height: slideHeight } = this.params!.slideDimensions;
        return {
            left: Math.round(region.x * slideWidth),
            top: Math.round(region.y * slideHeight),
            width: Math.round(region.width * slideWidth),
            height: Math.round(region.height * slideHeight),
        };
    }
    
    /**
     * Resolve context items to actual content
     * 
     * Handles different content types:
     * - as_*: AlphaSense results (text)
     * - logo_*, wikimedia_*, asset_*: Images (metadata + base64)
     * - color_*: Brand colors (hex values)
     * - page_*, excelread_*: Document content (text)
     * - artifact_*: Excel artifacts (metadata)
     * 
     * @param contextItems Array of context items to resolve
     * @returns Resolved context with text summaries and image IDs
     */
    private resolveContextItems(contextItems: ContextItem[]): ResolvedContextData {
        const resolver = getContextResolver();
        const contentById = new Map<string, ResolvedContent | null>();
        const imageIds: string[] = [];
        const summaryParts: string[] = [];
        
        for (const item of contextItems) {
            const itemSummaries: string[] = [];
            
            for (const contextId of item.context) {
                // Resolve the content
                const resolved = resolver.resolve(contextId);
                contentById.set(contextId, resolved);
                
                if (!resolved) {
                    itemSummaries.push(`[${contextId}]: Not found`);
                    continue;
                }
                
                // Check if this is an image type
                const prefix = resolved.prefix;
                if ((IMAGE_CONTEXT_PREFIXES as readonly string[]).includes(prefix)) {
                    // Check if image exists in cache
                    if (resolver.hasImage(contextId)) {
                        imageIds.push(contextId);
                        // Get metadata for description
                        const image = resolver.getImage(contextId);
                        const dims = image?.metadata?.dimensions as { width?: number; height?: number } | undefined;
                        const dimsStr = dims ? ` (${dims.width}x${dims.height})` : '';
                        itemSummaries.push(`[${contextId}]: Image${dimsStr} - see attached image`);
                    } else {
                        // Image-type but no image cached, use metadata
                        itemSummaries.push(`[${contextId}]: ${this.formatContentForPrompt(resolved.content)}`);
                    }
                } else {
                    // Text-based content
                    itemSummaries.push(`[${contextId}]: ${this.formatContentForPrompt(resolved.content)}`);
                }
            }
            
            if (itemSummaries.length > 0) {
                summaryParts.push(`**${item.purpose}**:\n${itemSummaries.join('\n')}`);
            }
        }
        
        return {
            contentById,
            imageIds,
            textSummary: summaryParts.join('\n\n'),
        };
    }
    
    /**
     * Format content for inclusion in prompt
     */
    private formatContentForPrompt(content: unknown): string {
        if (typeof content === 'string') {
            // Truncate long strings
            return content.length > 500 ? content.slice(0, 500) + '...' : content;
        }
        
        if (content && typeof content === 'object') {
            // Handle known content types
            const obj = content as Record<string, unknown>;
            
            // AlphaSense results
            if (obj.text && typeof obj.text === 'string') {
                const text = obj.text as string;
                return text.length > 500 ? text.slice(0, 500) + '...' : text;
            }
            
            // Brand colors
            if (obj.metadata && (obj.metadata as Record<string, unknown>).colors) {
                const colors = (obj.metadata as Record<string, unknown>).colors as Array<{ hex: string; type: string }>;
                return `Brand colors: ${colors.map(c => `${c.type}: ${c.hex}`).join(', ')}`;
            }
            
            // Excel artifact
            if (obj.artifactType) {
                return `Excel ${obj.artifactType}: ${obj.source ? JSON.stringify(obj.source) : 'data available'}`;
            }
            
            // Generic object - stringify with truncation
            const json = JSON.stringify(content);
            return json.length > 500 ? json.slice(0, 500) + '...' : json;
        }
        
        return String(content);
    }
    
    /**
     * Generate system prompt for the execution agent
     * 
     * Uses the new standardized prompt structure.
     */
    public generateSystemPrompt(_resolvedContext?: ResolvedContextItem[]): string {
        const params = this.params;
        if (!params) {
            return "You are a slide builder agent. Build slides area-by-area.";
        }
        
        // Clear any previous resolved images
        this.resolvedImageIds = [];
        
        // Resolve global and per-slide context to determine images
        let globalImageIds: string[] = [];
        if (params.globalContext && params.globalContext.length > 0) {
            const resolved = this.resolveContextItems(params.globalContext);
            globalImageIds = resolved.imageIds;
        }
        
        const allSlideImageIds: string[] = [...globalImageIds];
        for (const slide of this.slidesToBuild) {
            if (slide.context.length > 0) {
                const resolved = this.resolveContextItems(slide.context);
                allSlideImageIds.push(...resolved.imageIds);
            }
        }
        
        // Store all resolved image IDs for buildInitialMessages to use
        this.resolvedImageIds = [...new Set(allSlideImageIds)];
        
        // Import the builder function
        const { buildExecutionAgentSystemPrompt } = require('./executionAgent/prompt');
        
        return buildExecutionAgentSystemPrompt({
            sectionTitle: params.sectionTitle,
            slidesToBuild: this.slidesToBuild,
            slideDimensions: params.slideDimensions,
            styleGuideSlideCount: params.styleGuideSlideCount ?? 0,
            overallPlan: params.overallPlan,
            globalContext: params.globalContext,
            referenceSlideIndices: params.referenceSlideIndices,
            excelContext: params.excelContext,
            styleGuideInfo: params.styleGuideInfo,
            tools: this.toolCalls,
            hasImages: this.resolvedImageIds.length > 0,
            imageIds: this.resolvedImageIds,
        });
    }
    
    /**
     * Build initial messages for the tool loop
     * 
     * Creates a comprehensive "context message" with:
     * 1. Task overview - what slides to build
     * 2. Context items - all research, logos, artifacts with images
     * 3. Reference slides - images of hidden style guide slides
     * 4. Visual guides - Gemini-generated blueprints for each slide
     * 5. Instructions - how to proceed
     * 
     * This message is NOT stripped by MessageBuilder - it's real content.
     */
    protected buildInitialMessages(params: ExecutionAgentParams, _resolvedContext?: ResolvedContextItem[]): ChatMessage[] {
        const resolver = getContextResolver();
        const contentParts: (TextContentPart | ImageContentPart)[] = [];
        
        // ═══════════════════════════════════════════════════════════════════════
        // SECTION 1: Task Overview
        // ═══════════════════════════════════════════════════════════════════════
        
        contentParts.push({
            type: 'text',
            text: `## Task: Build ${this.slidesToBuild.length} Slides

${this.slidesToBuild.map((s, i) => `**Slide ${i + 1}** (index ${s.actualSlideIndex}): "${s.title}"
- Purpose: ${s.purpose}
- Areas: ${s.areas.map(a => `${a.areaId} (${a.contentType})`).join(', ')}`).join('\n\n')}

Build each slide area-by-area using the context and references provided below.`,
        });
        
        // ═══════════════════════════════════════════════════════════════════════
        // SECTION 2: Context Items (with images)
        // ═══════════════════════════════════════════════════════════════════════
        
        const hasGlobalContext = params.globalContext && params.globalContext.length > 0;
        const hasSlideContext = this.slidesToBuild.some(s => s.context && s.context.length > 0);
        
        if (hasGlobalContext || hasSlideContext) {
            contentParts.push({
                type: 'text',
                text: '\n\n---\n\n## Context Items\n\nUse these context items when building slides:',
            });
            
            // Global context
            if (hasGlobalContext) {
                contentParts.push({
                    type: 'text',
                    text: '\n\n### Global Context (applies to all slides)',
                });
                
                for (const item of params.globalContext!) {
                    this.addContextItemToMessage(contentParts, item, resolver);
                }
            }
            
            // Per-slide context
            for (let i = 0; i < this.slidesToBuild.length; i++) {
                const slide = this.slidesToBuild[i];
                if (slide.context && slide.context.length > 0) {
                    contentParts.push({
                        type: 'text',
                        text: `\n\n### Context for Slide ${i + 1}: "${slide.title}"`,
                    });
                    
                    for (const item of slide.context) {
                        this.addContextItemToMessage(contentParts, item, resolver);
                    }
                }
            }
        }
        
        // ═══════════════════════════════════════════════════════════════════════
        // SECTION 3: Reference Slides (hidden style guide slides)
        // ═══════════════════════════════════════════════════════════════════════
        
        if (this.referenceSlideData.size > 0) {
            contentParts.push({
                type: 'text',
                text: '\n\n---\n\n## Reference Slides (Style Guide)\n\nUse these hidden style guide slides as visual references for styling, layout, and density:',
            });
            
            // Sort by index for consistent ordering
            const sortedIndices = Array.from(this.referenceSlideData.keys()).sort((a, b) => a - b);
            
            for (const index of sortedIndices) {
                const data = this.referenceSlideData.get(index)!;
                const shapeCount = (data.slideData as { shapes?: unknown[] })?.shapes?.length || 0;
                
                contentParts.push({
                    type: 'text',
                    text: `\n\n**Reference Slide ${index}** (${shapeCount} shapes)`,
                });
                
                // Add the slide image
                contentParts.push({
                    type: 'image_url',
                    image_url: {
                        url: ensureDataUrl(data.imageBase64),
                        detail: 'auto',
                    },
                });
                
                console.log(`[ExecutionAgent] Included reference slide ${index} image`);
            }
        }
        
        // ═══════════════════════════════════════════════════════════════════════
        // SECTION 4: Visual Guides (Gemini blueprints)
        // ═══════════════════════════════════════════════════════════════════════
        
        if (params.visualGuides && params.visualGuides.length > 0) {
            contentParts.push({
                type: 'text',
                text: '\n\n---\n\n## Visual Guides (Gemini Blueprints)\n\nBuild each slide to match these AI-generated visual targets. The guides show the expected layout, density, and structure:',
            });
            
            // Sort by slideIndex for clear ordering
            const sortedGuides = [...params.visualGuides].sort((a, b) => a.slideIndex - b.slideIndex);
            
            for (const guide of sortedGuides) {
                const slideInfo = this.slidesToBuild[guide.slideIndex];
                
                contentParts.push({
                    type: 'text',
                    text: `\n\n**Visual Guide for Slide ${guide.slideIndex + 1}: "${slideInfo?.title || 'Unknown'}"**${
                        guide.instructions ? `\nInstructions: ${guide.instructions.substring(0, 200)}...` : ''
                    }`,
                });
                
                contentParts.push({
                    type: 'image_url',
                    image_url: {
                        url: ensureDataUrl(guide.imageBase64),
                        detail: 'high', // Higher detail for blueprints
                    },
                });
                
                console.log(`[ExecutionAgent] Included visual guide for slide ${guide.slideIndex + 1}`);
            }
        }
        
        // ═══════════════════════════════════════════════════════════════════════
        // SECTION 5: Instructions
        // ═══════════════════════════════════════════════════════════════════════
        
        contentParts.push({
            type: 'text',
            text: `\n\n---\n\n## Instructions

1. Start by reading working slide 1 with \`readSlide\` to see its current state
2. Build each area using \`buildArea\`, following the visual guides and reference slides above
3. Check the screenshot after each build to verify quality
4. If something looks wrong, use \`rollback\` and try again
5. When ALL slides are built and verified, call \`submitResults\`

**Remember:** Match the density, styling, and structure shown in the visual guides and reference slides.`,
        });
        
        return [{
            role: 'user' as const,
            content: contentParts,
        }];
    }
    
    /**
     * Add a context item (with its resolved content/images) to the message parts
     */
    private addContextItemToMessage(
        contentParts: (TextContentPart | ImageContentPart)[],
        item: ContextItem,
        resolver: ReturnType<typeof getContextResolver>
    ): void {
        // Add the purpose and IDs
        contentParts.push({
            type: 'text',
            text: `\n\n**${item.purpose}**\nContext IDs: ${item.context.join(', ')}`,
        });
        
        for (const contextId of item.context) {
            const resolved = resolver.resolve(contextId);
            
            if (!resolved) {
                contentParts.push({
                    type: 'text',
                    text: `\n- [${contextId}]: Not found`,
                });
                continue;
            }
            
            // Check if this is an image type (uses shared constant)
            if ((IMAGE_CONTEXT_PREFIXES as readonly string[]).includes(resolved.prefix) && resolver.hasImage(contextId)) {
                const imageData = resolver.getImage(contextId);
                
                if (imageData) {
                    contentParts.push({
                        type: 'text',
                        text: `\n- [${contextId}]: Image`,
                    });
                    contentParts.push({
                        type: 'image_url',
                        image_url: {
                            url: `data:${imageData.mimeType};base64,${imageData.base64}`,
                            detail: 'auto',
                        },
                    });
                    console.log(`[ExecutionAgent] Included context image: ${contextId}`);
                } else {
                    contentParts.push({
                        type: 'text',
                        text: `\n- [${contextId}]: Image (not in cache)`,
                    });
                }
            } else {
                // Text content - format it
                contentParts.push({
                    type: 'text',
                    text: `\n- [${contextId}]: ${this.formatContentForPrompt(resolved.content)}`,
                });
            }
        }
    }
    
    /**
     * Track area results from tool calls and inject slide state feedback
     * 
     * After each buildArea call, we fetch the current slide state and inject
     * a feedback message with the slide breakdown and image. This enables
     * the agent to evaluate its progress and decide what to do next.
     */
    protected async onToolResult(toolName: string, result: unknown): Promise<void> {
        if (toolName === 'buildArea' && result && typeof result === 'object') {
            const buildResult = result as BuildAreaResponse & { slideIndex?: number; areaId?: string; areaDescription?: string };
            const slideIndex = buildResult.slideIndex ?? -1;
            
            // Track detailed area result
            this.areaResults.push({
                slideIndex,
                areaId: buildResult.areaId ?? 'unknown',
                areaDescription: buildResult.areaDescription ?? 'unknown',
                checkpointId: buildResult.checkpointId ?? '00000',
                success: buildResult.success ?? false,
                shapesCreated: buildResult.shapeCount ?? 0,
                errors: buildResult.errors ?? [],
            });
            
            // Update slide result
            const slideResult = this.slideResults.get(slideIndex);
            if (slideResult) {
                slideResult.areasBuilt++;
                if (buildResult.success) {
                    slideResult.finalCheckpointId = buildResult.checkpointId;
                } else {
                    slideResult.errors = [
                        ...(slideResult.errors ?? []),
                        ...(buildResult.errors ?? []),
                    ];
                }
            }
            
            // Generate and queue slide state feedback
            await this.queueSlideStateFeedback(slideIndex, buildResult);
        }
    }
    
    /**
     * Queue slide state feedback after a buildArea call.
     * 
     * This reads the current slide state from the backend and creates
     * a feedback message with the detailed slide JSON and screenshot.
     * The message is wrapped in reminder tags so it can be stripped
     * from older messages to save context tokens.
     */
    private async queueSlideStateFeedback(
        slideIndex: number,
        buildResult: BuildAreaResponse
    ): Promise<void> {
        // Timeout for readWorkingSlide (15 seconds)
        const SLIDE_READ_TIMEOUT_MS = 15000;
        
        try {
            // Read the current slide state from the backend with timeout
            const slideDataPromise = backendClient.readWorkingSlide(this.planId, slideIndex);
            const timeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(() => reject(new Error(`readWorkingSlide timed out after ${SLIDE_READ_TIMEOUT_MS}ms`)), SLIDE_READ_TIMEOUT_MS);
            });
            
            const slideData = await Promise.race([slideDataPromise, timeoutPromise]);
            
            if (!slideData.success) {
                console.warn(`[ExecutionAgent] Failed to read slide ${slideIndex} for feedback: ${slideData.error}`);
                return;
            }
            
            // Get slide info for context
            const slideInfo = this.slidesToBuild.find(s => s.actualSlideIndex === slideIndex);
            const slideTitle = slideInfo?.title || `Slide ${slideIndex + 1}`;
            const totalAreas = slideInfo?.areas.length || 0;
            const areasBuiltForSlide = this.areaResults.filter(r => r.slideIndex === slideIndex).length;
            
            // Generate feedback content using the template function
            const feedbackContent = generateSlideStateFeedbackContent({
                slideTitle,
                slideIndex,
                areasBuiltForSlide,
                totalAreas,
                success: buildResult.success,
                errors: buildResult.errors,
                shapeCount: buildResult.shapeCount,
                checkpointId: buildResult.checkpointId,
                slideJson: JSON.stringify(slideData.slide_data, null, 2),
            });
            
            // Wrap in reminder tags and add image marker using MessagePreparer
            const preparer = getMessagePreparer();
            const feedbackText = preparer.wrapInReminderTag(
                `${feedbackContent}\n\n${REMINDER_IMAGE_MARKER}`
            );
            
            // Build the content parts (text + image)
            const contentParts: (TextContentPart | ImageContentPart)[] = [
                { type: 'text', text: feedbackText },
            ];
            
            // Add the slide screenshot if available
            if (slideData.screenshot_base64) {
                contentParts.push({
                    type: 'image_url',
                    image_url: {
                        url: ensureDataUrl(slideData.screenshot_base64),
                        detail: 'high',
                    },
                });
            }
            
            // Create the feedback message
            const feedbackMessage: ChatMessage = {
                role: 'user',
                content: contentParts,
            };
            
            // Queue the feedback message for injection
            const messageBuilder = getMessageBuilder();
            messageBuilder.queueFeedbackMessage(feedbackMessage, 'ExecutionAgent.slideStateFeedback');
            
            console.log(`[ExecutionAgent] Queued slide state feedback for slide ${slideIndex} (${areasBuiltForSlide}/${totalAreas} areas built)`);
            
        } catch (error) {
            console.error(`[ExecutionAgent] Error generating slide state feedback:`, error);
            // Non-fatal - continue without feedback
        }
    }
    
    /**
     * Process submitted results from the submitResults tool
     */
    protected async processSubmittedResults(
        submittedResults: SubmitResultsParams,
        _params: ExecutionAgentParams,
        _context: {
            params: ExecutionAgentParams;
            threadId: ThreadId;
            taskId: string;
            cancellationToken: CancellationToken;
            resolvedContext: ResolvedContextItem[];
            conversationId: string;
            documentId: string;
            callbacks?: AgentCallbacks;
        }
    ): Promise<ResultItem<ExecutionAgentResponse>[]> {
        console.log(`[ExecutionAgent] Processing submitted results: ${submittedResults.results.length} items`);
        
        // Mark slides as successful based on tracked results
        for (const slideResult of this.slideResults.values()) {
            // A slide is successful if it has at least one area built and no errors
            slideResult.success = slideResult.areasBuilt > 0 && (slideResult.errors?.length ?? 0) === 0;
        }
        
        // Build the response
        const slideResultsArray = Array.from(this.slideResults.values());
        const response: ExecutionAgentResponse = {
            sectionId: this.params?.sectionId,
            sectionTitle: this.params?.sectionTitle,
            slidesBuilt: this.slidesToBuild.length,
            slideResults: slideResultsArray,
            areaResults: this.areaResults,
            success: slideResultsArray.every(r => r.success),
            errors: slideResultsArray.flatMap(r => r.errors ?? []),
        };
        
        return [{
            requirementId: 'slides_build',
            relevantResults: response,
            rationale: submittedResults.summary ?? `Built ${response.slidesBuilt} slides with ${this.areaResults.length} areas`,
        }];
    }
    
    // buildStylePatternSection moved to prompt/index.ts
    
    /**
     * Build final result - uses processSubmittedResults via base class
     * Only called if submitResults wasn't used (fallback)
     */
    protected override buildFinalResult(
        results: ResultItem<unknown>[],
        threadId: ThreadId,
        tokensUsed?: number
    ): AgentResult<ExecutionAgentResponse> {
        // If we have results from processSubmittedResults, use those
        if (results.length > 0 && results[0]?.relevantResults) {
            const response = results[0].relevantResults as ExecutionAgentResponse;
            if (response.success) {
                return this.buildResult(results as ResultItem<ExecutionAgentResponse>[], threadId, tokensUsed);
            } else {
                return this.buildPartialResult(results as ResultItem<ExecutionAgentResponse>[], threadId, tokensUsed);
            }
        }
        
        // Fallback: build from tracked state (shouldn't happen with submitResults pattern)
        console.log(`[ExecutionAgent] Building fallback result from tracked state`);
        
        const slideResultsArray = Array.from(this.slideResults.values());
        const response: ExecutionAgentResponse = {
            sectionId: this.params?.sectionId,
            sectionTitle: this.params?.sectionTitle,
            slidesBuilt: this.slidesToBuild.length,
            slideResults: slideResultsArray,
            areaResults: this.areaResults,
            success: this.areaResults.length > 0 && this.areaResults.every(r => r.success),
            errors: this.areaResults.flatMap(r => r.errors ?? []),
        };
        
        const resultItem: ResultItem<ExecutionAgentResponse> = {
            requirementId: 'slides_build',
            relevantResults: response,
            rationale: `Built ${this.areaResults.length} areas across ${this.slidesToBuild.length} slides`,
        };
        
        if (response.success) {
            return this.buildResult([resultItem], threadId, tokensUsed);
        } else {
            return this.buildPartialResult([resultItem], threadId, tokensUsed);
        }
    }
}

export default ExecutionAgent;
